import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { SportConfig, getCurrentSportConfig, getSportConfig } from '../config/sportConfigs';

interface ThemeContextType {
  currentSport: SportConfig;
  setSport: (sportId: string) => void;
  theme: {
    colors: SportConfig['branding']['colorScheme'];
    typography: SportConfig['branding']['typography'];
    sportIcon: string;
    appName: string;
  };
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
  initialSport?: string;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ 
  children, 
  initialSport = 'pickleball' 
}) => {
  const [currentSport, setCurrentSport] = useState<SportConfig>(() => {
    // Domain to sport mapping for redirects
    const domainMappings: { [key: string]: string } = {
      'mypickleballcoach.online': 'pickleball',
      'www.mypickleballcoach.online': 'pickleball',
      'mygolfgamecoach.online': 'golf',
      'www.mygolfgamecoach.online': 'golf',
      'mytennisgamecoach.online': 'tennis',
      'www.mytennisgamecoach.online': 'tennis',
      'myhikingcoach.online': 'hiking',
      'www.myhikingcoach.online': 'hiking'
    };

    // Check if we need to redirect based on domain
    const currentDomain = window.location.hostname;
    const domainSport = domainMappings[currentDomain];
    
    if (domainSport) {
      // Redirect to main domain with sport parameter
      const redirectUrl = `https://www.mysportscoach.online?sport=${domainSport}`;
      console.log('Redirecting from', currentDomain, 'to', redirectUrl);
      window.location.replace(redirectUrl);
      return getSportConfig(domainSport); // Return something while redirecting
    }
    
    // Check URL parameters first, then path (e.g. /recovery-rehab/pickleball)
    const urlParams = new URLSearchParams(window.location.search);
    let urlSport = urlParams.get('sport');
    if (!urlSport) {
      const pathMatch = window.location.pathname.match(/^\/recovery-rehab\/([^/]+)/);
      if (pathMatch) urlSport = pathMatch[1];
    }
    
    // Try to get sport from URL params first, then localStorage, then environment, then default
    const storedSport = localStorage.getItem('selectedSport');
    const sportId = urlSport || storedSport || initialSport || import.meta.env.VITE_SPORT || 'pickleball';
    
    // Debug logging
    console.log('ThemeContext - Current domain:', currentDomain);
    console.log('ThemeContext - Domain sport:', domainSport);
    console.log('ThemeContext - URL sport:', urlSport);
    console.log('ThemeContext - Stored sport:', storedSport);
    console.log('ThemeContext - Final sport ID:', sportId);
    
    // Get sport config using the imported function
    const sportConfig = getSportConfig(sportId);
    console.log('ThemeContext - Sport config:', sportConfig);
    
    return sportConfig;
  });

  const setSport = useCallback((sportId: string) => {
    const newSport = getSportConfig(sportId);
    setCurrentSport(newSport);
    localStorage.setItem('selectedSport', sportId);
  }, []);

  // Handle URL parameter and path changes (sync theme from URL)
  useEffect(() => {
    const handleUrlChange = () => {
      const urlParams = new URLSearchParams(window.location.search);
      let urlSport = urlParams.get('sport');
      if (!urlSport) {
        const pathMatch = window.location.pathname.match(/^\/recovery-rehab\/([^/]+)/);
        if (pathMatch) urlSport = pathMatch[1];
      }
      if (urlSport && urlSport !== currentSport.id) {
        setSport(urlSport);
      }
    };

    window.addEventListener('popstate', handleUrlChange);
    handleUrlChange();

    return () => window.removeEventListener('popstate', handleUrlChange);
  }, [currentSport.id, setSport]);

  // Apply theme to document when sport changes
  useEffect(() => {
    const root = document.documentElement;
    const colors = currentSport.branding.colorScheme;
    
    // Sport accent - used ONLY for: active tab, category tags, progress indicators
    // Header/nav/primary buttons use --shared-* (MendOnBend family)
    root.style.setProperty('--color-sport-accent', colors.primary);
    root.style.setProperty('--primary-color', colors.primary);
    root.style.setProperty('--secondary-color', colors.secondary);
    root.style.setProperty('--accent-color', colors.accent);
    root.style.setProperty('--color-primary', colors.primary);
    root.style.setProperty('--color-secondary', colors.secondary);
    root.style.setProperty('--color-surface', colors.surface);
    root.style.setProperty('--color-border', colors.border);
    root.style.setProperty('--color-text', colors.text);
    root.style.setProperty('--color-text-secondary', colors.textSecondary);
    /* Use shared background/surface for MendOnBend alignment - not sport colors */
    root.style.setProperty('--background-color', 'var(--shared-background)');
    root.style.setProperty('--surface-color', 'var(--shared-surface)');
    root.style.setProperty('--text-color', colors.text);
    root.style.setProperty('--text-secondary-color', colors.textSecondary);
    root.style.setProperty('--success-color', colors.success);
    root.style.setProperty('--warning-color', colors.warning);
    root.style.setProperty('--error-color', colors.error);
    
    // Set typography
    root.style.setProperty('--font-family', currentSport.branding.typography.fontFamily);
    root.style.setProperty('--heading-font', currentSport.branding.typography.headingFont);
    root.style.setProperty('--body-font', currentSport.branding.typography.bodyFont);
    
    // Update document title
    document.title = currentSport.displayName;
    
    // Update favicon if available
    const favicon = document.querySelector('link[rel="icon"]') as HTMLLinkElement;
    if (favicon && currentSport.branding.logo) {
      favicon.href = currentSport.branding.logo;
    }
  }, [currentSport]);

  const theme = {
    colors: currentSport.branding.colorScheme,
    typography: currentSport.branding.typography,
    sportIcon: currentSport.sportIcon,
    appName: currentSport.displayName,
  };

  const value: ThemeContextType = {
    currentSport,
    setSport,
    theme,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const useSportConfig = (): SportConfig => {
  const { currentSport } = useTheme();
  return currentSport;
};
